/**
 * Client-fulfilled tool names — the single source of truth for which tool
 * names the server must treat as "no execute here; the browser supplies the
 * result via `addToolOutput`".
 *
 * Two namespaces exist today:
 *   - `app_<8hex>` — SEP-1865 app-provided tool aliases, generated by the
 *     client registry (`app-tools-registry.ts`) for tools an MCP app iframe
 *     advertised.
 *   - `ui_*` — WebMCP-shaped MCPJam UI tools, hand-named in the client
 *     catalog (`client/src/lib/webmcp/ui-tools-catalog.ts`) and validated
 *     server-side by `validateUiToolEntries`.
 *
 * Both the skip gate (`isSkippableClientFulfilledToolCall` in
 * `http-tool-calls.ts`) and the pause predicate
 * (`hasUnresolvedClientFulfilledToolCalls` in `mcpjam-stream-handler.ts`)
 * consume these so the two paths can never drift apart.
 */

export const APP_TOOL_ALIAS_REGEX = /^app_[a-z0-9]{8}$/i;

/**
 * UI tool names are curated, lowercase snake_case with a reserved `ui_`
 * prefix. Max 64 chars total — a strict subset of the Anthropic tool-name
 * charset (`^[a-zA-Z0-9_-]{1,64}$`).
 */
export const UI_TOOL_NAME_REGEX = /^ui_[a-z0-9][a-z0-9_]{0,60}$/;

export function isAppToolAlias(name: string): boolean {
  return APP_TOOL_ALIAS_REGEX.test(name);
}

export function isUiToolName(name: string): boolean {
  return UI_TOOL_NAME_REGEX.test(name);
}

/**
 * True when `name` belongs to a client-fulfilled namespace. Callers that gate
 * on "is this tool call resolved by the browser?" must use this rather than
 * matching one namespace's regex directly.
 */
export function isClientFulfilledToolName(name: string): boolean {
  return isAppToolAlias(name) || isUiToolName(name);
}

/**
 * Whether a UI tool call must pause for the user's approval this turn.
 *
 * Single source of truth for BOTH sides of the approval handshake: the
 * server (per-tool `needsApproval` in `buildUiTools`, and the free-model
 * loop's approval gate) and the client (the executor's defer-before-execute
 * gate). They must agree because the client decides "defer" when the
 * tool-call chunk arrives — BEFORE the server's approval-request chunk
 * reaches it.
 *
 * Read-only tools (`ui_snapshot_app`) never gate: they observe, so pausing
 * them buys no safety and costs a click.
 */
export function uiToolCallNeedsApproval(opts: {
  readOnly: boolean;
  requireToolApproval: boolean;
}): boolean {
  return opts.requireToolApproval && !opts.readOnly;
}
